import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { ProNavigation } from "@/components/ProNavigation";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function ApitherapyLibrary() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-hero py-8 px-4">
      <ProNavigation />
      
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between">
          <Button variant="outline" size="sm" onClick={() => navigate("/treatments-library")}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Bibliothèque</span>
            <span className="sm:hidden">Retour</span>
          </Button>
          <h1 className="text-xl sm:text-3xl font-bold text-primary">🐝 L'Apithérapie</h1>
          <div className="hidden sm:block w-[180px]" />
        </div>

        {/* Introduction */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">🐝 Soins par les produits de la ruche</CardTitle>
            <CardDescription>Médecine naturelle utilisant les trésors des abeilles</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <p className="text-muted-foreground">
                L'apithérapie est une médecine naturelle qui utilise les produits issus de la ruche pour prévenir 
                ou soigner divers maux. Ces produits ont tous des vertus puissantes et agissent en profondeur.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Badge variant="outline" className="text-base px-3 py-1">🍯 Miel</Badge>
                <Badge variant="outline" className="text-base px-3 py-1">🌿 Propolis</Badge>
                <Badge variant="outline" className="text-base px-3 py-1">🌾 Pollen</Badge>
                <Badge variant="outline" className="text-base px-3 py-1">👑 Gelée royale</Badge>
                <Badge variant="outline" className="text-base px-3 py-1">🐝 Venin d'abeille</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="miel" className="space-y-4">
          <TabsList className="flex w-full overflow-x-auto gap-1 h-auto flex-wrap sm:flex-nowrap">
            <TabsTrigger value="miel" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">🍯 Miel</TabsTrigger>
            <TabsTrigger value="pollen" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">🌾 Pollen</TabsTrigger>
            <TabsTrigger value="propolis" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">🌿 Propolis</TabsTrigger>
            <TabsTrigger value="gelee" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">👑 Gelée</TabsTrigger>
            <TabsTrigger value="venin" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">🐝 Venin</TabsTrigger>
          </TabsList>

          {/* MIEL */}
          <TabsContent value="miel">
            <Card>
              <CardHeader>
                <CardTitle>🍯 Le Miel</CardTitle>
                <CardDescription>Le nectar transformé par les abeilles</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold text-primary mb-2">🌸 Origine</h4>
                  <p className="text-muted-foreground">
                    Produit par les abeilles à partir du nectar des fleurs ou de sécrétions végétales, 
                    transformé et stocké dans les rayons.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-primary mb-2">🧬 Composition</h4>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>Eau, glucose, fructose, saccharose</li>
                    <li>Vitamines (notamment B), minéraux, oligo-éléments</li>
                    <li>Enzymes (dont glucose oxydase), acides organiques</li>
                    <li>Antioxydants (flavonoïdes), substances aromatiques</li>
                    <li>Traces de pollen, facteurs antibiotiques naturels</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-primary mb-2">💪 Propriétés thérapeutiques</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-start gap-2">
                      <span className="text-primary">⚡</span>
                      <span className="text-muted-foreground"><strong>Dynamisant</strong> : booste l'énergie et l'immunité</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🛡️</span>
                      <span className="text-muted-foreground"><strong>Antioxydant</strong> : surtout les miels foncés (ex. : sapin)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🦠</span>
                      <span className="text-muted-foreground"><strong>Antiseptique</strong> et anti-inflammatoire</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">😴</span>
                      <span className="text-muted-foreground"><strong>Sédatif</strong> : idéal en tisane le soir</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">💊</span>
                      <span className="text-muted-foreground"><strong>Anti-infectieux</strong> : empêche la prolifération bactérienne</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🩹</span>
                      <span className="text-muted-foreground"><strong>Cicatrisant</strong> : utilisé en pansements</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🍽️</span>
                      <span className="text-muted-foreground"><strong>Digestif</strong> et laxatif léger</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">❤️</span>
                      <span className="text-muted-foreground"><strong>Cardioprotecteur</strong> : prévient l'athérosclérose</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">✨</span>
                      <span className="text-muted-foreground"><strong>Cosmétique</strong> : hydrate et nourrit la peau</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-primary mb-3">🍯 Types de miel en apithérapie</h4>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="acacia">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <span>🌳</span>
                          <span className="font-semibold">Miel d'Acacia</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="list-disc list-inside text-muted-foreground space-y-1">
                          <li>Doux, bon pour les nourrissons</li>
                          <li>Soulage la constipation</li>
                          <li>Bien toléré par les diabétiques</li>
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="thym">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <span>🌿</span>
                          <span className="font-semibold">Miel de Thym</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="list-disc list-inside text-muted-foreground space-y-1">
                          <li>Antiseptique puissant</li>
                          <li>Idéal pour les maladies infectieuses</li>
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="eucalyptus">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <span>🌬️</span>
                          <span className="font-semibold">Miel d'Eucalyptus</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="list-disc list-inside text-muted-foreground space-y-1">
                          <li>Voies respiratoires</li>
                          <li>Toux, infections digestives</li>
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="oranger">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <span>🌼</span>
                          <span className="font-semibold">Miel de Fleur d'Oranger</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="list-disc list-inside text-muted-foreground space-y-1">
                          <li>Calmant naturel</li>
                          <li>Troubles du sommeil</li>
                          <li>Stress, migraines</li>
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* POLLEN */}
          <TabsContent value="pollen">
            <Card>
              <CardHeader>
                <CardTitle>🌾 Le Pollen – Un Superaliment de la Ruche</CardTitle>
                <CardDescription>Produit végétal récolté par les abeilles, riche en nutriments essentiels</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold text-primary mb-2">💪 Propriétés thérapeutiques</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🌞</span>
                      <div>
                        <strong className="text-foreground">Anti-déprime & stimulant</strong>
                        <p className="text-muted-foreground">Agit comme euphorisant naturel, redonne de l'énergie (activation de l'ADP → énergie cellulaire)</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🍽️</span>
                      <div>
                        <strong className="text-foreground">Digestif & prébiotique</strong>
                        <p className="text-muted-foreground">Régule le transit intestinal, nourrit la flore intestinale</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🦴</span>
                      <div>
                        <strong className="text-foreground">Anti-rachitique</strong>
                        <p className="text-muted-foreground">Favorise la calcification osseuse, utile pendant la croissance</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🧬</span>
                      <div>
                        <strong className="text-foreground">Anti-âge</strong>
                        <p className="text-muted-foreground">Riche en vitamine B3, lutte contre le vieillissement cellulaire, prévention de la chute de cheveux</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🔬</span>
                      <div>
                        <strong className="text-foreground">Soutien de la prostate</strong>
                        <p className="text-muted-foreground">Réduit les effets de l'hypertrophie bénigne de la prostate</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🛡️</span>
                      <div>
                        <strong className="text-foreground">Antiseptique naturel</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-primary/5 rounded-lg p-4">
                  <h4 className="font-semibold text-primary mb-2">🍯 Zoom : Le Pain d'Abeille</h4>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>Mélange de pollen + enzymes + sécrétions salivaires d'abeilles</li>
                    <li>Fermenté dans la ruche 🐝</li>
                    <li>Combine les bienfaits du pollen et du miel</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-primary mb-2">🥄 Conseils de consommation</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <strong className="text-foreground">Formes :</strong>
                      <ul className="list-disc list-inside text-muted-foreground">
                        <li><strong>Frais</strong> : à conserver au réfrigérateur ou congélateur ❄️</li>
                        <li><strong>Sec</strong> : à réhydrater (tremper une nuit dans un peu d'eau)</li>
                      </ul>
                    </div>
                    <div>
                      <strong className="text-foreground">Dosage recommandé :</strong>
                      <ul className="list-disc list-inside text-muted-foreground">
                        <li>1 à 2 cuillères à soupe par jour</li>
                        <li>En cure d'un mois</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="text-foreground font-medium">
                    ✅ À retenir : Le pollen est un concentré de vitalité : énergisant, régulateur, protecteur et réparateur. 
                    Un allié précieux en naturopathie !
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PROPOLIS */}
          <TabsContent value="propolis">
            <Card>
              <CardHeader>
                <CardTitle>🌿 La Propolis – Bouclier naturel de la ruche</CardTitle>
                <CardDescription>Résine végétale aux propriétés antimicrobiennes puissantes</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold text-primary mb-2">🌳 Origine</h4>
                  <p className="text-muted-foreground">
                    La propolis est une résine végétale issue des bourgeons et jeunes pousses de certains arbres 
                    (peuplier, bouleau, conifères). Les abeilles la récoltent pour assainir et protéger la ruche.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-primary mb-2">🧬 Composition</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <Badge variant="outline">55% résines & gommes</Badge>
                    <Badge variant="outline">30% cire</Badge>
                    <Badge variant="outline">5-10% huiles essentielles</Badge>
                    <Badge variant="outline">5% pollen</Badge>
                  </div>
                  <p className="text-muted-foreground mt-2">+ vitamines, acides aromatiques, flavonoïdes, oligo-éléments</p>
                </div>

                <div>
                  <h4 className="font-semibold text-primary mb-2">💪 Propriétés thérapeutiques</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🦠</span>
                      <div>
                        <strong className="text-foreground">Antibiotique naturel</strong>
                        <p className="text-muted-foreground">Bactériostatique (bloque la prolifération), Antivirale (empêche virus de libérer leur matériel génétique)</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🤒</span>
                      <div>
                        <strong className="text-foreground">Infections saisonnières</strong>
                        <p className="text-muted-foreground">Très utile en hiver ❄️ : rhumes, angines, gastro-entérites, toux, grippe</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">😮‍💨</span>
                      <div>
                        <strong className="text-foreground">Anesthésiant local</strong>
                        <p className="text-muted-foreground">Apaise les maux de gorge immédiatement (ex. spray buccal)</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🍄</span>
                      <div>
                        <strong className="text-foreground">Antifongique</strong>
                        <p className="text-muted-foreground">Cible les mycoses cutanées (candida albicans, aspergillus)</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🛡️</span>
                      <div>
                        <strong className="text-foreground">Immunostimulant</strong>
                        <p className="text-muted-foreground">Stimule la production d'anticorps et de lymphocytes</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">✨</span>
                      <div>
                        <strong className="text-foreground">Cicatrisante</strong>
                        <p className="text-muted-foreground">Accélère la régénération cellulaire</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">♻️</span>
                      <div>
                        <strong className="text-foreground">Détoxifiante & hépatoprotectrice</strong>
                        <p className="text-muted-foreground">Protège le foie (soutien en cas de prise de médicaments, alcool…)</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-primary mb-2">🧴 Formes d'utilisation</h4>
                  <div className="flex flex-wrap gap-2">
                    <Badge>💧 Teinture-mère</Badge>
                    <Badge>🌬️ Spray nasal/buccal</Badge>
                    <Badge>🍬 Pastilles à sucer</Badge>
                    <Badge>🍯 Sirops</Badge>
                    <Badge>🧴 Pommades/onguents</Badge>
                    <Badge>🧼 Dentifrice, shampoing</Badge>
                  </div>
                </div>

                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="text-foreground font-medium">
                    ✅ À retenir : La propolis est un véritable antibiotique naturel polyvalent : antiviral, antifongique, 
                    cicatrisant, et protecteur du foie. Un must-have de la trousse naturopathique !
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* GELÉE ROYALE */}
          <TabsContent value="gelee">
            <Card>
              <CardHeader>
                <CardTitle>👑 La Gelée Royale – L'élixir vital de la ruche</CardTitle>
                <CardDescription>Nourriture exclusive de la reine des abeilles</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold text-primary mb-2">🐝 Origine & Description</h4>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>Nourriture exclusive de la reine des abeilles 👑</li>
                    <li>Donnée aussi aux larves les premiers jours</li>
                    <li>Aspect : gélatineux / crémeux</li>
                    <li>Couleur : blanc → jaune pâle</li>
                    <li>Goût : légèrement sucré avec une touche amère</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-primary mb-2">🧬 Composition</h4>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">💧 Eau</Badge>
                    <Badge variant="outline">🍬 Glucides</Badge>
                    <Badge variant="outline">🍗 Protides</Badge>
                    <Badge variant="outline">🧈 Lipides</Badge>
                    <Badge variant="outline">🔬 Vitamines B (B5, B9, B12)</Badge>
                  </div>
                  <p className="text-muted-foreground mt-2">+ environ 3% de matière indéterminée (encore à l'étude)</p>
                </div>

                <div>
                  <h4 className="font-semibold text-primary mb-2">💪 Propriétés thérapeutiques</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <span className="text-primary">⚡</span>
                      <div>
                        <strong className="text-foreground">Revitalisante</strong>
                        <p className="text-muted-foreground">Aide à la convalescence, améliore la récupération après fatigue ou surmenage, soutien du métabolisme cérébral (mémoire, concentration, humeur)</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🛡️</span>
                      <div>
                        <strong className="text-foreground">Immunostimulante</strong>
                        <p className="text-muted-foreground">Renforce les défenses naturelles, peut être aussi efficace qu'un antibiotique naturel 💥</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🧬</span>
                      <div>
                        <strong className="text-foreground">Antioxydante</strong>
                        <p className="text-muted-foreground">Neutralise les radicaux libres</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">❤️</span>
                      <div>
                        <strong className="text-foreground">Cardiovasculaire</strong>
                        <p className="text-muted-foreground">Augmente le taux d'hémoglobine, bénéfique pour la circulation sanguine</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">😌</span>
                      <div>
                        <strong className="text-foreground">Anti-douleur</strong>
                        <p className="text-muted-foreground">Stimule la production d'endorphines (effet bien-être)</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-primary mb-2">🥄 Conseils de consommation</h4>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>🕗 Le matin à jeun</li>
                    <li>1 g par jour, à laisser fondre sous la langue</li>
                    <li>💊 Cure de 3 semaines → pause de 8 jours → possible 1 à 2 fois/an</li>
                  </ul>
                </div>

                <div className="bg-destructive/10 rounded-lg p-4">
                  <p className="text-destructive font-medium">
                    ⚠️ Contre-indication : Allergie aux produits de la ruche
                  </p>
                </div>

                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="text-foreground font-medium">
                    ✅ À retenir : La gelée royale est un concentré naturel d'énergie, d'immunité et de longévité. 
                    Un joyau thérapeutique, surtout en cas de fatigue physique ou mentale !
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* VENIN */}
          <TabsContent value="venin">
            <Card>
              <CardHeader>
                <CardTitle>🐝💉 Le Venin d'Abeille – L'arme secrète de la ruche</CardTitle>
                <CardDescription>Puissant remède naturel à manier avec précaution</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-destructive/10 rounded-lg p-4">
                  <p className="text-destructive font-medium">
                    ⚠️ Origine : Injecté par l'abeille via son dard, souvent au péril de sa vie. 
                    Il existe une méthode de prélèvement sans tuer l'abeille (encore coûteuse et peu répandue).
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-primary mb-2">🔬 Composition</h4>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">💥 Mélittine (anti-inflammatoire puissant)</Badge>
                    <Badge variant="outline">🧪 Enzymes</Badge>
                    <Badge variant="outline">🧬 Acides aminés</Badge>
                    <Badge variant="outline">🍬 Glucides</Badge>
                    <Badge variant="outline">⚛️ Apamine et Adolapine</Badge>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-primary mb-2">💪 Propriétés thérapeutiques</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-start gap-2">
                      <span className="text-primary">💢</span>
                      <span className="text-muted-foreground"><strong>Anti-inflammatoire</strong></span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">💊</span>
                      <span className="text-muted-foreground"><strong>Antalgique</strong> (soulage la douleur)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🦴</span>
                      <span className="text-muted-foreground"><strong>Efficace contre les rhumatismes</strong></span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">🧠</span>
                      <span className="text-muted-foreground"><strong>Aide en cas de sclérose en plaques</strong></span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">❤️</span>
                      <span className="text-muted-foreground"><strong>Cardiotonique</strong> (soutient la fonction cardiaque)</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-primary mb-2">🧑‍⚕️ Modes d'administration</h4>
                  <div className="space-y-3">
                    <div className="bg-muted/50 rounded-lg p-3">
                      <strong className="text-foreground">Piqûres localisées :</strong>
                      <p className="text-muted-foreground">Une abeille est déposée sur la zone à traiter 🐝➡️👋</p>
                      <p className="text-destructive text-sm">⚠️ Risque de réaction allergique sévère (choc anaphylactique possible)</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3">
                      <strong className="text-foreground">Injection à la seringue :</strong>
                      <p className="text-muted-foreground">Technique utilisée par certains professionnels</p>
                      <p className="text-muted-foreground text-sm">💉 Moins répandue et nécessite un cadre médical sécurisé</p>
                    </div>
                  </div>
                </div>

                <div className="bg-destructive/10 rounded-lg p-4 space-y-2">
                  <h4 className="font-semibold text-destructive">⚠️ Précautions essentielles</h4>
                  <ul className="list-disc list-inside text-destructive space-y-1">
                    <li>Toujours effectué par un professionnel expérimenté</li>
                    <li>Éviter en cas d'allergie aux piqûres d'abeilles</li>
                    <li>Ne jamais pratiquer seul</li>
                  </ul>
                </div>

                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="text-foreground font-medium">
                    ✅ À retenir : Le venin d'abeille est un puissant remède naturel, à manier avec précaution. 
                    Son usage thérapeutique peut offrir des résultats spectaculaires… entre des mains expertes !
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Conclusion */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>🧠 Conclusion générale sur l'apithérapie</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Les produits de la ruche 🌼 (miel, propolis, pollen, gelée royale, venin) ont tous des vertus puissantes</li>
              <li>Ils agissent en profondeur et peuvent être utilisés en cure chaque année</li>
              <li>⚠️ Toujours choisir des produits de qualité, d'origine vérifiée</li>
              <li>Tous les produits ne se valent pas : leur efficacité dépend de l'abeille, de la plante butinée, et du lieu de récolte</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
